import React from 'react';
import { 
    Trophy, Eye, CalendarCheck, TrendingUp, LifeBuoy, 
    Award, Gem, Users, Star, Zap 
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

// Badge icon mapping
const BADGE_ICONS = {
    suivi_elite: Trophy,
    transparence: Eye,
    regularite: CalendarCheck,
    star_montante: TrendingUp,
    sauveur_mandat: LifeBuoy,
    pro: Award,
    diamant: Gem,
    team_excellence: Users,
    premier_avis: Star,
    dix_avis: Zap
};

// Badge colors
const BADGE_COLORS = {
    suivi_elite: '#FFD700',
    transparence: '#3B82F6',
    regularite: '#10B981',
    star_montante: '#F59E0B',
    sauveur_mandat: '#EF4444',
    pro: '#8B5CF6',
    diamant: '#B9F2FF',
    team_excellence: '#681793',
    premier_avis: '#6B7280',
    dix_avis: '#F97316'
};

/**
 * Single Badge component
 */
export function Badge({ badge, size = 'md', showTooltip = true }) {
    const Icon = BADGE_ICONS[badge.id] || Award;
    const color = BADGE_COLORS[badge.id] || badge.color || '#6B7280';
    
    const sizeClasses = {
        sm: 'w-8 h-8',
        md: 'w-10 h-10',
        lg: 'w-12 h-12'
    };
    
    const iconSizes = {
        sm: 'w-4 h-4',
        md: 'w-5 h-5',
        lg: 'w-6 h-6'
    };

    const content = (
        <div 
            className={cn(
                'rounded-full flex items-center justify-center',
                sizeClasses[size]
            )}
            style={{ backgroundColor: `${color}20`, border: `2px solid ${color}` }}
        >
            <Icon className={iconSizes[size]} style={{ color }} />
        </div>
    );

    if (!showTooltip) return content;

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    {content}
                </TooltipTrigger>
                <TooltipContent>
                    <p className="font-semibold">{badge.name}</p>
                    <p className="text-xs text-muted-foreground">{badge.description}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

/**
 * BadgeList - Display multiple badges
 */
export function BadgeList({ badges, size = 'md', maxDisplay = 5, className }) {
    if (!badges || badges.length === 0) {
        return (
            <div className={cn('text-sm text-muted-foreground', className)}>
                Aucun badge pour le moment
            </div>
        );
    }

    const displayBadges = badges.slice(0, maxDisplay);
    const remaining = badges.length - maxDisplay;

    return (
        <div className={cn('flex items-center gap-2 flex-wrap', className)}>
            {displayBadges.map((badge, idx) => (
                <Badge key={badge.id || idx} badge={badge} size={size} />
            ))}
            {remaining > 0 && (
                <span className="text-sm text-muted-foreground">+{remaining}</span>
            )}
        </div>
    );
}

/**
 * BadgeProgress - Show progress towards a badge
 */
export function BadgeProgress({ badge, progress }) {
    const Icon = BADGE_ICONS[badge.id] || Award;
    const color = BADGE_COLORS[badge.id] || badge.color || '#6B7280';
    const percent = progress?.percent || 0;
    const isEarned = percent >= 100;

    return (
        <div className={cn(
            'p-4 rounded-lg border',
            isEarned ? 'bg-green-50 border-green-200' : 'bg-card'
        )}>
            <div className="flex items-center gap-3 mb-2">
                <div 
                    className={cn(
                        'w-10 h-10 rounded-full flex items-center justify-center',
                        isEarned ? 'opacity-100' : 'opacity-50'
                    )}
                    style={{ backgroundColor: `${color}20`, border: `2px solid ${color}` }}
                >
                    <Icon className="w-5 h-5" style={{ color }} />
                </div>
                <div className="flex-1">
                    <p className="font-medium">{badge.name}</p>
                    <p className="text-xs text-muted-foreground">{badge.description}</p>
                </div>
                {isEarned && (
                    <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded">
                        Obtenu !
                    </span>
                )}
            </div>
            
            {!isEarned && progress && (
                <div className="mt-3">
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>{progress.current} / {progress.target}</span>
                        <span>{Math.round(percent)}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                            className="h-full transition-all"
                            style={{ width: `${percent}%`, backgroundColor: color }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * BadgeGrid - Full badge display with progress
 */
export function BadgeGrid({ earnedBadges = [], allBadges = [], className }) {
    return (
        <div className={cn('space-y-4', className)}>
            {earnedBadges.length > 0 && (
                <div>
                    <h4 className="font-semibold mb-2">Badges obtenus ({earnedBadges.length})</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {earnedBadges.map((badge, idx) => (
                            <BadgeProgress 
                                key={badge.id || idx} 
                                badge={badge} 
                                progress={{ percent: 100 }} 
                            />
                        ))}
                    </div>
                </div>
            )}
            
            {allBadges.filter(b => !earnedBadges.find(e => e.id === b.id)).length > 0 && (
                <div>
                    <h4 className="font-semibold mb-2">Badges à débloquer</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {allBadges
                            .filter(b => !earnedBadges.find(e => e.id === b.id))
                            .map((badge, idx) => (
                                <BadgeProgress 
                                    key={badge.id || idx} 
                                    badge={badge} 
                                    progress={badge.progress || { percent: 0, current: 0, target: 1 }} 
                                />
                            ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default BadgeList;
